import { computed, useContext } from "@nuxtjs/composition-api";
import { sharedRef, useVSFContext, Logger } from "@vue-storefront/core";
import { COOKIES } from '~/helpers/constants';
import { useSalesCrm, useSapcc } from '~/composables';
import { useUserStore } from '~/stores/user-store'
import { userCheckoutStore } from "~/stores/checkout-store";
import { getCMSLabels } from "~/helpers/globalLabels";

export interface UserDataInterface {
  u: string // UID
  r: string, // session cookie
  fn: string, // first name
  ln: string, // last name
  e: string,  // email
}

const useUserAccount = (id: string = 'user-account') => {
  const genericError = getCMSLabels('genericerror');
  // Loads context used to call API endpoint
  const context = useVSFContext();
  const { i18n } = useContext();
  const useCheckoutStore = userCheckoutStore();
  const appKey = i18n.localeProperties.appKey;
  const baseStoreId = i18n.localeProperties.baseStoreId;
  // Shared ref holding the response from the API
  const result = sharedRef(null, `auth-${id}`);

  //store order history
  const orderHistory = sharedRef(null, `orderHistory`);

  // Shared ref indicating whether any method is waiting for the data from the API
  const loading = sharedRef(false, `auth-loading-${id}`);

  const error = sharedRef({ auth: null }, `auth-error-${id}`);

  const cookiesUserData = context.$cookies.get(COOKIES.USER_DATA);

  const logoutSuccessfullMessage = getCMSLabels('logout-successfully');
  const recaptchaFlag = getCMSLabels('enablecaptcha');

  // setting up default group to 6001, if no membership info available in CDC first login response after signup
  const moominMembershipDefaultGroup = getCMSLabels('moominMembershipDefaultGroup') || 6001;
  
  const userData = sharedRef<UserDataInterface>({
    u: cookiesUserData? cookiesUserData?.u : '',
    r: cookiesUserData? cookiesUserData?.r : '',
    fn: cookiesUserData? cookiesUserData?.fn : '',
    ln: cookiesUserData? cookiesUserData?.ln : '',
    e: cookiesUserData? cookiesUserData?.e : ''
  }, `current-user-data`);

  const isAuthenticated = sharedRef(context.$cookies.get(COOKIES.USER) === COOKIES.CURRENT_USER_ID && !!context.$cookies.get(COOKIES.USER_DATA), `is-authenticated`);

  // Shared ref holding users metadata
  const userMetaData = sharedRef(null, `user-meta-data`);

  const userStore = useUserStore()

  const { load: loadSalesCrm, result: crmTokenResult } = useSalesCrm('generateToken');

  const { load: loadSapccToken, result: sapccTokenValue  } = useSapcc('generateSapccToken');

  const { result: cartDetails, loadShoppingCart: loadCart } = useSapcc('cartMain');

  const verifyCaptcha = async () => {
    let token;

    if (recaptchaFlag && recaptchaFlag.toLowerCase() === 'true') {
      token = await context.$recaptcha.getResponse();
    } else {
      token = ''
    }

    return token;
  }

  const destroyCaptcha = () => {
    if (recaptchaFlag && recaptchaFlag.toLowerCase() === 'true') {
      context.$recaptcha.reset();
    }
  }
  const setCookieAfterLoggedin = (loginResponse) => {
    if (loginResponse && loginResponse.status === 200) {
      context.$cookies.set(
        COOKIES.USER,
        COOKIES.CURRENT_USER_ID,
        {
          maxAge: 60 * 60 * 24 * 1,
          path: "/",
          secure: true
        }
      );
      //set user info
      const responseUserData = {
        u: loginResponse.data.UID, // this is logged in user id
        r: loginResponse.encryptedToken,
        fn: loginResponse.data.profile?.firstName,
        ln: loginResponse.data.profile?.lastName,
        e: loginResponse.data.profile.email,
        ph: loginResponse.data.data?.phoneNumber
      }

      Object.assign(userData.value, responseUserData);

      context.$cookies.set(COOKIES.USER_DATA, JSON.stringify(responseUserData));
      userMetaData.value = loginResponse?.data || {};

      userStore.addUserMetaData(userMetaData.value)

    }
  }

  const verifyCaptchaAndRegister = async (params) => {
    try {
      loading.value = true;
      const token = await verifyCaptcha();
      const registerResponse = await context.$vsf.$myaccount.api.register({ body: params, ct: token });
      setCookieAfterLoggedin(registerResponse);
      return registerResponse;
    } catch (e) {
      error.value = e
      return e;
    } finally {
      destroyCaptcha();
    }
  };

  const verifyCaptchaAndLogin = async (params) => {
    try {
      const token = await verifyCaptcha();
      loading.value = true;
      const loginResponse = await context.$vsf.$myaccount.api.login({ body: params, ct: token });
      if (loginResponse.data?.UID) {
        await loadSapccToken();

        if (sapccTokenValue.value === null) {
          error.value = {
            status: 401,
            message: genericError,
          }
        } else {
          const userGroup = loginResponse.data.data?.membership?.[0]?.affiliationMembership || moominMembershipDefaultGroup
          const setAccountResponse = await context.$vsf.$sapccvolatile.api.setAccountInfo({ userId: loginResponse.data.UID, userGroup: userGroup, token: sapccTokenValue.value, baseStoreId });

          if (setAccountResponse?.errors) { //access token error
            error.value = setAccountResponse.errors.length > 0 ? setAccountResponse.errors[0] : setAccountResponse.errors
            Object.assign(error.value, { status: 403 });
          }
        }
      }

      if (loginResponse.status === 200) {
        setCookieAfterLoggedin(loginResponse);
        // destroyCaptcha();
        result.value = loginResponse
        return loginResponse;
      } else {
        return error.value;
      }

    } catch (e) {
      error.value = e
      console.error('User login with captcha error ==> ', e);
      return e;
    } finally {
      destroyCaptcha();
    }
  };
  const getAccountInfo = async () => {
    try {
      loading.value = true;
      const sessionCookieData = context.$cookies.get(COOKIES.USER_DATA);
      if (sessionCookieData) {
        const getAccountInfoResponse = await context.$vsf.$myaccount.api.getAccountInfo({u: sessionCookieData.u, r:sessionCookieData.r});
        if (getAccountInfoResponse && getAccountInfoResponse.statusCode === 200) {
          loading.value = false;

          userData.value = {
            ...userData.value,
            fn: getAccountInfoResponse.profile?.firstName || '',
            ln: getAccountInfoResponse.profile?.lastName || '',
            e: getAccountInfoResponse.profile?.email || ''
          }
          return {
            statusCode: getAccountInfoResponse.statusCode,
            statusReason: getAccountInfoResponse.statusReason,
            data: {
             firstName: getAccountInfoResponse.profile?.firstName || '',
             lastName: getAccountInfoResponse.profile?.lastName || '',
             email: getAccountInfoResponse.profile?.email
            }
          }
        } else {
          loading.value = false;
          return {
            statusCode: getAccountInfoResponse?.statusCode,
            statusReason: getAccountInfoResponse?.statusReason,
          }
        }
      } else {
        loading.value = false;
        return {
          statusCode: 400,
          statusReason: "NOT OK",
          message: genericError
        }
      }
    } catch (e) {
      error.value = e
      console.error('Error to get account info ==> ', e);
      return e;
    }
  };


  const getCrmTokenAndUid = async () => {
    const sessionData = context.$cookies.get(COOKIES.USER_DATA);

    if (sessionData) {
      await loadSalesCrm();
      let crmToken = crmTokenResult.value || '';

    //If CRM token is generated properly
      if (crmToken) {
        return {
          uid: sessionData.u,
          crmToken: crmToken
        }
      }
    } else {
      return {
        status: 404,
        message: 'User not found!'
      }
    }
  };

  const updatePersonalInfo = async (params: { firstName: string, lastName: string, countryCode: string, token: string}) => {

      try {
        loading.value = true;
        const uidToken = await getCrmTokenAndUid();
        if (uidToken?.status === 404) {
          loading.value = false;
          return uidToken;
        }

        let body = {
          externalId: uidToken.uid,
          consumerCredential: "CDC",
          firstName: params.firstName,
          lastName: params.lastName,
            addresses: [
                {
                    isPrimary: true,
                    addressType: 'Billing',
                    countryCode: params.countryCode,
                    lastname: params.lastName,
                    firstname:params.firstName
                }
            ]
        };

        let responseData;

        const responsePersonalData =
          await context.$vsf.$myaccount.api.updatePersonalInfo({
            body: body,
            token: uidToken.crmToken,
          });

        if (responsePersonalData && responsePersonalData.errorMessage && responsePersonalData.errorMessage.errorCode === '500') { //failure
          responseData = {
            status: parseInt(responsePersonalData.errorMessage?.errorCode),
            message: responsePersonalData.errorMessage?.errorMessage,
            data: null
          }
        } else if(responsePersonalData?.errorCode === 'INVALID_SESSION_ID') { //Wrong auth token
          responseData = {
            status: 401,
            message: responsePersonalData?.message,
            errorCode: responsePersonalData?.errorCode,
            data: null
          }
        } else { //success
          responseData = {
            status: 200,
            message: 'Success',
            data: {
              email: responsePersonalData.account.email,
            }
          }
        }

        userData.value = {
          ...userData.value,
          fn: params.firstName,
          ln: params.lastName
        }
        context.$cookies.set(COOKIES.USER_DATA, JSON.stringify(userData.value));

        result.value = responseData;
        return responseData;
      } catch (err) {
        error.value.api = err;
        Logger.error(`updatePersonalInfo Error ==>>  ${error.value.api}`);
        return { errorMessage: error.value.api };
      } finally {
        loading.value = false;
      }

  };


  const updateEmail = async (params: { newEmail: String, password: String }) => {

    try {
      loading.value = true;
      const uidToken = await getCrmTokenAndUid();

      if (uidToken?.status === 404) {
        loading.value = false;
        return uidToken;
      }

      let responseData;
      const sessionCookieData = context.$cookies.get(COOKIES.USER_DATA);
      if (sessionCookieData) {
      const loginRes = await login({
        "email": sessionCookieData.e,
        "password": params.password
      });

      if (loginRes?.status === 200 && loginRes?.data) {
        //After successful login
        if (loginRes.data.UID === uidToken.uid) {
          const bodyParam = {
            Marketing_Program_Subscription__r :
            {
                Azure_Id__c : uidToken.uid
            },
            New_Email__c : params.newEmail,
            Require_New_Email_Verification__c : false,
            Require_Old_Email_Verification__c : false
          }

          const responsePersonalData =
          await context.$vsf.$myaccount.api.updateEmail({
            body: bodyParam,
            token: uidToken.crmToken,
          });

          if (responsePersonalData?.success) { //success
            responseData = {
              status: 200,
              message: 'Success',
              errorCode: null
            }

            await removeLoginEmail({email: userData.value.e});

            userData.value = {
              ...userData.value,
              e: params.newEmail
            }
            context.$cookies.set(COOKIES.USER_DATA, JSON.stringify(userData.value));
          }

          if(responsePersonalData?.errorCode) { //Wrong auth token
            responseData = {
              status: 401,
              message: responsePersonalData?.message,
              errorCode: responsePersonalData?.errorCode,
            }
          }
        } else { // login token does not math with current session token
          responseData = {
            status: 401,
            message: 'Authentication failed!',
          }
        }

      } else { // login not successful
        responseData = {
          status: 401,
          message: genericError,
        }
      }
    } else { //Not get user cookies
      responseData = {
        status: 401,
        message: "User not found!"
      }
    }
      loading.value = false;
      return responseData;
    } catch (err) {
      error.value.api = err;
      Logger.error(`Update Email Error ==>>  ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  const getShippingBillingAddress = async () => {

    try {
      loading.value = true;
      const uidToken = await getCrmTokenAndUid();

      if (uidToken?.status === 404) {
        loading.value = false;
        return uidToken;
      }

      let responseData;
      const addressResponse =
      await context.$vsf.$myaccount.api.getShippingBillingAddress({
        uid: uidToken.uid,
        token: uidToken.crmToken,
      });

      if(addressResponse?.errorCode) { //error occur
        responseData = {
          status: 401,
          message: addressResponse?.message,
          errorCode: addressResponse?.errorCode,
        }
      } else if (addressResponse?.errorMessage &&  addressResponse?.account === null) { //Wrong UID or no subscription found
        responseData = {
          status: parseInt(addressResponse?.errorMessage?.errorCode) || 401,
          message: addressResponse?.errorMessage?.errorMessage || 'No data found!'
        }
      } else { //success
        const allAddresses = addressResponse?.account?.addresses || []
        if (allAddresses && allAddresses.length > 0) {
          const billingAddresses = allAddresses.filter(address => address.addressType === 'Billing')
          const shippingAddresses = allAddresses.filter(address => address.addressType === 'Shipping')
          responseData = {
            status: 200,
            message: 'Success',
            salesforceId: addressResponse?.account?.salesforceId,
            billingAddresses: billingAddresses,
            shippingAddresses: shippingAddresses
          }
        } else {
          responseData = {
            status: 401,
            message: 'Addresses not found'
          }
        }
      }

      return responseData;
    } catch (err) {
      error.value.api = err;
      Logger.error(`[My account] Get shipping billing error ==>>  ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  const addEditBillingShipping = async (params) => {

    try {
      loading.value = true;
      const uidToken = await getCrmTokenAndUid();

      if (uidToken?.status === 404) {
        loading.value = false;
        return uidToken;
      }

      const payloadParams = {
        ExternalId: uidToken.uid,
        salesforceId:params.salesforceId,
        RequireEmailVerification: false,
        FirstName: params.firstName,
        LastName: params.lastName,
          Addresses: [
          {
            UpdateId: params.updateId ? params.updateId : '',
            DeleteRecord: false,
            AddressType: params.addressType,
            isPrimary: params.isPrimary,
            FirstName: params.firstName,
            LastName: params.lastName,
            Phone: params.phone,
            title: params.addressTitle ? params.addressTitle : params.street,
            Street: params.street,
            Street2: params?.street2 || '',
            City: params.city,
            PostalCode: params.postCode,
            CountryCode: params.countryCode,
            StateCode: params.stateCode,
          }
        ]
      }

      let responseData;
      const addressResponse =
      await context.$vsf.$myaccount.api.addUpdateShippingBilling({
        uid: uidToken.uid,
        token: uidToken.crmToken,
        body: payloadParams
      });

      if(addressResponse?.errorCode) { //error occur
        responseData = {
          status: 401,
          message: addressResponse?.message,
          errorCode: addressResponse?.errorCode,
        }
      } else if (addressResponse?.errorMessage &&  addressResponse?.account === null) { //Wrong UID or no subscription found
        responseData = {
          status: parseInt(addressResponse?.errorMessage?.errorCode) || 401,
          message: addressResponse?.errorMessage?.errorMessage || 'No data found!'
        }
      } else { //success
        const allAddresses = addressResponse?.account?.addresses || []
        if (allAddresses && allAddresses.length > 0) {
          const billingAddresses = allAddresses.filter(address => address.addressType === 'Billing')
          const shippingAddresses = allAddresses.filter(address => address.addressType === 'Shipping')
          const msg = params.updateId ? "Update address successfully" : "Address added successfully";
          responseData = {
            status: 200,
            message: msg,
            billingAddresses: billingAddresses,
            shippingAddresses: shippingAddresses
          }
        } else {
          responseData = {
            status: 401,
            message: 'Addresses not found'
          }
        }
      }

      return responseData;
    } catch (err) {
      error.value.api = err;
      Logger.error(`[My account] add/update shipping billing error ==>>  ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  const deleteBillingShipping = async (deleteParams) => {

    try {
      loading.value = true;
      const uidToken = await getCrmTokenAndUid();

      if (uidToken?.status === 404) {
        loading.value = false;
        return uidToken;
      }

      let responseData;
      const deleteAddressResponse =
      await context.$vsf.$myaccount.api.addUpdateShippingBilling({
        uid: uidToken.uid,
        token: uidToken.crmToken,
        body: {
          ExternalId: uidToken.uid,
          salesforceId:deleteParams.salesforceId,
            Addresses: [
            {
              UpdateId: deleteParams.addressId,
              DeleteRecord: true,
              AddressType: deleteParams.addressType,
              isPrimary: deleteParams.isPrimary
            }
          ]
        }
      });
      if(deleteAddressResponse?.errorCode) { //error occur
        responseData = {
          status: 401,
          message: deleteAddressResponse?.message,
          errorCode: deleteAddressResponse?.errorCode,
        }
      } else if (deleteAddressResponse?.errorMessage &&  deleteAddressResponse?.account === null) { //Wrong UID or no subscription found
        responseData = {
          status: parseInt(deleteAddressResponse?.errorMessage?.errorCode) || 401,
          message: deleteAddressResponse?.errorMessage?.errorMessage || 'No data found!'
        }
      } else { //success
        const allAddresses = deleteAddressResponse?.account?.addresses || []
        if (allAddresses && allAddresses.length > 0) {
          const billingAddresses = allAddresses.filter(address => address.addressType === 'Billing')
          const shippingAddresses = allAddresses.filter(address => address.addressType === 'Shipping')
          responseData = {
            status: 200,
            message: 'Delete Address Successfully!',
            billingAddresses: billingAddresses,
            shippingAddresses: shippingAddresses
          }
        } else {
          responseData = {
            status: 401,
            message: 'Addresses not found'
          }
        }
      }

      return responseData;
    } catch (err) {
      error.value.api = err;
      Logger.error(`[My account] Get shipping billing error ==>>  ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  const updatePassword = async (params) => {
    try {
      loading.value = true;
      const sessionCookieData = context.$cookies.get(COOKIES.USER_DATA);
      if (sessionCookieData) {
        const updatePasswordResponse = await context.$vsf.$myaccount.api.updatePassword({uid: sessionCookieData.u, regToken:sessionCookieData.r, password: params.password, newPassword: params.newPassword});

        if (updatePasswordResponse && updatePasswordResponse.statusCode === 200) {
          loading.value = false;
          return {
            status: updatePasswordResponse.statusCode,
            message: 'Password updated successfully.'
          }
        } else if (updatePasswordResponse && updatePasswordResponse.statusCode === 403) {
          loading.value = false;
          return {
            status: updatePasswordResponse.statusCode,
            message: 'you\'ve not entered your current password correctly'
          }
        } else {
          loading.value = false;
          return {
            status: updatePasswordResponse?.statusCode,
            message: updatePasswordResponse?.errorMessage,
            errorCode: updatePasswordResponse?.errorCode,
          }
        }
      } else {
        loading.value = false;
        return {
          status: 400,
          message: genericError
        }
      }
    } catch (e) {
      error.value = e
      console.error('Error on update password ::: ', e);
      return e;
    }
  };
  const removeLoginEmail = async (params) => {
    try {
      loading.value = true;

      const sessionCookieData = context.$cookies.get(COOKIES.USER_DATA);
      if (sessionCookieData) {
        const removeEmailResponse = await context.$vsf.$myaccount.api.removeLoginEmail({uid: sessionCookieData.u, regToken:sessionCookieData.r, email: params.email});

        if (removeEmailResponse && removeEmailResponse.statusCode === 200) {
          loading.value = false;
          return {
            status: removeEmailResponse.statusCode,
            message: 'Removed email.'
          }
        } else if (removeEmailResponse && removeEmailResponse.statusCode === 403) {
          loading.value = false;
          return {
            status: removeEmailResponse.statusCode,
            message: genericError
          }
        } else {
          loading.value = false;
          return {
            status: removeEmailResponse?.statusCode,
            message: removeEmailResponse?.errorMessage,
            errorCode: removeEmailResponse?.errorCode,
          }
        }
      } else {
        loading.value = false;
        return {
          status: 400,
          message: genericError
        }
      }
    } catch (e) {
      error.value = e
      console.error('Error on removing email ::: ', e);
      return e;
    }
  };

  // Get order history
  const getOrderHistory = async (params: {purchaseChannel: String}) => {
    try {
      loading.value = true;
      const uidToken = await getCrmTokenAndUid();

      if (uidToken?.status === 404) {
        loading.value = false;
        return uidToken;
      }

      const orderResponse =
      await context.$vsf.$myaccount.api.getOrderHistory({
        uid: uidToken.uid,
        token: uidToken.crmToken,
        purchaseChannel: params.purchaseChannel.toUpperCase()
      });

      if(orderResponse?.errorCode) { //error occur when session expired
        orderHistory.value = {
          status: 401,
          message: orderResponse?.message,
          errorCode: orderResponse?.errorCode,
        }
      } else if (orderResponse?.errorMessage &&  orderResponse?.orders === null) { //Wrong UID or no subscription found
        orderHistory.value = {
          status: parseInt(orderResponse?.errorMessage?.errorCode) || 401,
          message: orderResponse?.errorMessage?.errorMessage || 'No data found!'
        }
      } else { //success
        const orderData = (orderResponse?.orders || []).map((entry) => {

          const { totalGiftWrapAmount, totalGiftWrapItems } = entry.orderLines.reduce(
            (totals, el) => {
              if (el.serviceProducts && el.serviceProducts.length > 0) {
                totals.totalGiftWrapAmount += el.serviceProducts[0]?.totalPriceWithoutTax || 0;
                totals.totalGiftWrapItems++;
              }
              return totals;
            },
            { totalGiftWrapAmount: 0, totalGiftWrapItems: 0 }
          );

          entry.totalGiftWrapItems = totalGiftWrapItems;
          entry.totalGiftWrapAmount = totalGiftWrapAmount;
          return entry;
        });

        orderHistory.value = {
          status: 200,
          orders: orderData
        }
      }
      return orderHistory.value

    } catch (err) {
      error.value.api = err;
      Logger.error(`[My account] Get order history error ==>>  ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };


  const handleAfterLoginCalls = async () => {
    await getAccountInfo();
    loading.value = true;
    await loadCart();
    useCheckoutStore.removeAllProductsInCart(appKey);
    cartDetails.value?.entries?.map(p => {
      useCheckoutStore.setProductsInCart({ appKey, pCode: p.product.code });
    })
    useCheckoutStore.setCartItemsCount(cartDetails.value?.entries?.map(p => p.quantity).reduce((a, b) => a + b, 0) || 0)
    userStore.updateUserStatus(true);
  }


  //Register composable method
  const register = async (params) => {
    try {

      if (!params?.lastName?.trim() || !params?.firstName?.trim()) {
        return Logger.error(`User Registration Error: First and last name are required.`);
      }

      loading.value = true;
      params.baseStoreId = baseStoreId;
      params.mmg = moominMembershipDefaultGroup;
      const registerUser = await verifyCaptchaAndRegister(params);

      if (registerUser && registerUser.status === 200) {
        await handleAfterLoginCalls();
      }
      return registerUser;
    } catch (err) {
      error.value.api = err;
      Logger.error(`User Registration Error ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  //Login composable method
  const login = async (params) => {
    try {
      const loginUser = await verifyCaptchaAndLogin(params);
      if (loginUser && loginUser.status === 200) {
        await handleAfterLoginCalls()
      }
      context.$cookies.remove(`${appKey}kopt`);
      return loginUser;
    } catch (err) {
      error.value.api = err;
      Logger.error(`User Login Error ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  const logout = async () => {
    try {
      loading.value = true;
      const sessionCookieData = context.$cookies.get(COOKIES.USER_DATA);
      if (sessionCookieData) {
        const appKey = i18n.localeProperties.appKey;
        context.$cookies.remove(COOKIES.USER_DATA);
        context.$cookies.remove(COOKIES.USER);
        context.$cookies.remove(COOKIES.SALESFORCE_CRM_TOKEN);
        context.$cookies.remove(appKey + "#cGUID");
        context.$cookies.remove(appKey + "#cUID");
        context.$cookies.remove(appKey + "#ci");
        context.$cookies.remove("vsf-cart-main");
        context.$cookies.remove(`${appKey}kopt`);
        useCheckoutStore.resetAllData(appKey);
        userStore.removeUserData();
        userStore.updateUserStatus(false);
        loading.value = false;
        return {
          status: 200,
          message: logoutSuccessfullMessage
        }
      } else {
        loading.value = false;
        return {
          status: 400,
          message: "No loggedin user found."
        }
      }
    } catch (e) {
      error.value = e
      console.error('Error on logout ::: ', e);
      return e;
    }
  };

  const resetPassword = async (params) => {
    const passwordResetToken = params.passwordResetToken;
    const newPassword = params.newPassword;
    const ct = params.ct ?? '';


    try {
      loading.value = true;
      const response = await context.$vsf.$myaccount.api.resetPassword({passwordResetToken: passwordResetToken , newPassword: newPassword, ct});
      result.value = response;

      if (result.value.statusCode == 200) {

      const resultFinal = {
        status: result.value.statusCode,
        statusReason: result.value.statusReason,
        data: {
          result: result.value,
        },
        message: "Reset Password Successfull.",
      };

      return resultFinal;
    } else {

      const resultFinal = {
        status: result.value.statusCode,
        statusReason: result.value.statusReason,
        errorCode: result.value.errorCode,
        errorMessage: result.value.errorMessage,
        data: {
          result: result.value,
        },
        message: "Invalid token",
      };

      return resultFinal;

    }
    } catch (err) {
      error.value.api = err;
      Logger.error(`Error on reset password: ${error.value.api}`);
      return {errorMessage: error.value.api};
    } finally {
      loading.value = false;
    }
  };

  return {
    register,
    login,
    getAccountInfo,
    updatePersonalInfo,
    updateEmail,
    updatePassword,
    removeLoginEmail,
    getShippingBillingAddress,
    addEditBillingShipping,
    deleteBillingShipping,
    getOrderHistory,
    logout,
    recaptchaFlag,
    verifyCaptcha,
    destroyCaptcha,
    resetPassword,
    isAuthenticated: computed(() => isAuthenticated.value),
    userData: computed(() => userData.value),
    userMetaData: computed(() => userMetaData.value),
    result: computed(() => result.value),
    orderHistory: computed(() => orderHistory.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value)
  };
};
export default useUserAccount;
